/**
 * AssetDationForm — o BEM dado em pagamento (dação de bens).
 *
 * ⚠️ Era uma LISTA, com "Adicionar bem" e remoção por value. Agora é UM bem por pagamento: quem
 * precisa registrar outro bem cria outro pagamento de dação. A razão é de rastreabilidade — com
 * vários bens num lançamento só, o pagamento tinha um total que não voltava para as peças, e
 * identificar qual bem quitou qual título virava adivinhação. Um bem por pagamento dá a cada um o
 * seu valor, a sua data e a sua identificação.
 *
 * Por isso também não há mais estado de "expandido": não existe lista para colapsar, e os campos do
 * bem aparecem assim que o tipo de pagamento é escolhido.
 *
 * Os campos condicionais por `asset_type` continuam como estavam.
 */
import { AssetType, assetTypeLabels, AssetDationItem } from '../../../constants/payment';
import SelectTextField from '../SelectTextField';
import CurrencyInput from '../CurrencyInput';
import TextEdit from '../TextEdit';
import NumberEdit from '../NumberEdit';

interface Props {
    value: AssetDationItem;
    onChange: (item: AssetDationItem) => void;
    idPrefix?: string;
}

export const emptyAsset = (type: AssetType = AssetType.VEHICLE): AssetDationItem => ({
    asset_type: type,
    description: '',
    estimated_value: 0,
});

const assetTypeItems = Object.entries(assetTypeLabels).map(([value, label]) => ({ value, label }));

const LAND_TYPE_ITEMS = [
    { value: 'RURAL', label: 'Rural' },
    { value: 'URBAN', label: 'Urbano' },
];

const UNIT_ITEMS = [
    { value: 'TONELADAS', label: 'Toneladas' },
    { value: 'SACAS', label: 'Sacas' },
    { value: 'KG', label: 'Kg' },
    { value: 'LITROS', label: 'Litros' },
];

const toInt = (raw: string): number | undefined => {
    const n = parseInt(raw, 10);
    return Number.isNaN(n) ? undefined : n;
};

const toFloat = (raw: string): number | undefined => {
    const n = parseFloat(raw);
    return Number.isNaN(n) ? undefined : n;
};

const AssetDationForm: React.FC<Props> = ({ value, onChange, idPrefix = 'asset-dation' }) => {
    const update = (field: string, fieldValue: any) =>
        onChange({ ...value, [field]: fieldValue });

    return (
        <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Bem dado em pagamento
            </label>

            <div className="border dark:border-slate-600 rounded p-3 bg-gray-50 dark:bg-slate-700/50">
                <div className="space-y-2">
                    {/* Type selector + valor estimado (monetário) */}
                    <div className="grid grid-cols-2 gap-3">
                        <SelectTextField
                            label="Tipo de bem"
                            items={assetTypeItems}
                            value={value.asset_type}
                            emptyLabel="Selecione o tipo"
                            onChange={e => update('asset_type', e.target.value)}
                        />
                        <CurrencyInput
                            id={`${idPrefix}-estimated-value`}
                            label="Valor estimado (R$)"
                            prefix="R$"
                            fractionDigits={2}
                            value={value.estimated_value || 0}
                            onChange={num => update('estimated_value', num)}
                        />
                    </div>

                    {/* Description (all types) */}
                    <TextEdit
                        label="Descrição"
                        value={value.description}
                        onChange={e => update('description', e.target.value)}
                        placeholder=" "
                    />

                    {/* VEHICLE fields */}
                    {value.asset_type === AssetType.VEHICLE && (
                        <div className="grid grid-cols-3 gap-3">
                            <TextEdit label="Placa (Ex: ABC-1234)" value={value.plate || ''} onChange={e => update('plate', e.target.value)} placeholder=" " />
                            <TextEdit label="Chassi" value={value.chassis || ''} onChange={e => update('chassis', e.target.value)} placeholder=" " />
                            <TextEdit label="Renavam" value={value.renavam || ''} onChange={e => update('renavam', e.target.value)} placeholder=" " />
                            <TextEdit label="Marca" value={value.brand || ''} onChange={e => update('brand', e.target.value)} placeholder=" " />
                            <TextEdit label="Modelo" value={value.model || ''} onChange={e => update('model', e.target.value)} placeholder=" " />
                            <NumberEdit label="Ano" value={value.year || ''} onChange={e => update('year', toInt(e.target.value))} />
                        </div>
                    )}

                    {/* MACHINERY fields */}
                    {value.asset_type === AssetType.MACHINERY && (
                        <div className="grid grid-cols-2 gap-3">
                            <TextEdit label="Marca" value={value.brand || ''} onChange={e => update('brand', e.target.value)} placeholder=" " />
                            <TextEdit label="Modelo" value={value.model || ''} onChange={e => update('model', e.target.value)} placeholder=" " />
                            <TextEdit label="Nº Série" value={value.serial_number || ''} onChange={e => update('serial_number', e.target.value)} placeholder=" " />
                            <NumberEdit label="Ano" value={value.year || ''} onChange={e => update('year', toInt(e.target.value))} />
                        </div>
                    )}

                    {/* REAL_ESTATE fields */}
                    {value.asset_type === AssetType.REAL_ESTATE && (
                        <div className="grid grid-cols-2 gap-3">
                            <TextEdit label="Matrícula" value={value.registration_number || ''} onChange={e => update('registration_number', e.target.value)} placeholder=" " />
                            <TextEdit label="Cartório" value={value.registry_office || ''} onChange={e => update('registry_office', e.target.value)} placeholder=" " />
                            <TextEdit label="Área total (ha)" value={value.total_area ?? ''} inputMode="decimal" onChange={e => update('total_area', toFloat(e.target.value))} placeholder=" " />
                            <TextEdit label="Área edificada (m²)" value={value.built_area ?? ''} inputMode="decimal" onChange={e => update('built_area', toFloat(e.target.value))} placeholder=" " />
                            <div className="col-span-2">
                                <SelectTextField
                                    label="Tipo"
                                    items={LAND_TYPE_ITEMS}
                                    value={value.land_type || 'RURAL'}
                                    emptyLabel="Selecione"
                                    onChange={e => update('land_type', e.target.value)}
                                />
                            </div>
                        </div>
                    )}

                    {/* PRODUCT / GRAIN fields */}
                    {(value.asset_type === AssetType.PRODUCT || value.asset_type === AssetType.GRAIN) && (
                        <div className="grid grid-cols-2 gap-3">
                            <TextEdit label="Tipo de produto (Ex: Soja, Milho, Algodão)" value={value.product_type || ''} onChange={e => update('product_type', e.target.value)} placeholder=" " />
                            <TextEdit label="Quantidade" value={value.quantity ?? ''} inputMode="decimal" onChange={e => update('quantity', toFloat(e.target.value))} placeholder=" " />
                            <SelectTextField
                                label="Unidade"
                                items={UNIT_ITEMS}
                                value={value.unit || 'TONELADAS'}
                                emptyLabel="Selecione"
                                onChange={e => update('unit', e.target.value)}
                            />
                            <TextEdit label="Local de armazenamento" value={value.storage_location || ''} onChange={e => update('storage_location', e.target.value)} placeholder=" " />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AssetDationForm;
